import {
  GRADIENT_TYPE_IDS,
  gradientToLut,
  type Gradient,
  type GradientType,
} from '../core/gradient';
import { findLayerById, surfaceLayerIds } from '../core/layerTree';
import { rectAlignToPixels } from '../core/math';
import type { PatternImage } from '../core/patterns';
import { warpPoint, type WarpMesh } from '../core/warp';
import type { AdjustmentState, BlendMode, DocumentMeta, Point, RGBA, Rect } from '../core/types';
import { RenderTarget } from '../gl/renderTarget';
import { Texture } from '../gl/texture';
import type { Mat3 } from '../core/math';
import { strokeCoverage, type StrokeLocation } from '../selection/stroke';
import type { FilterId } from '../filters';
import { adjustmentPass } from './adjustPass';
import type { Engine } from './engine';
import { filterPass } from './filterPass';
import { floodFill } from './floodFill';
import {
  layerMaskOp,
  pixelOp,
  selectionOp,
  surfaceOp,
  type EngineHistoryOp,
  type FullSnapshot,
} from './historyOps';
import { healRegion } from './paintEffects';
import { surfaceStrokeTarget } from './strokeTarget';

const FULL_RECT = (doc: DocumentMeta): Rect => ({
  x: 0,
  y: 0,
  width: doc.width,
  height: doc.height,
});

function layerOffset(doc: DocumentMeta, layerId: string): Point {
  return findLayerById(doc.layers, layerId)?.contentOffset ?? { x: 0, y: 0 };
}

/** Selection / document rect → layer-local pixels for history snapshots. */
function toLayerLocalRect(rect: Rect, offset: Point, width: number, height: number): Rect {
  return rectAlignToPixels(
    {
      x: rect.x - offset.x,
      y: rect.y - offset.y,
      width: rect.width,
      height: rect.height,
    },
    width,
    height,
  );
}

/**
 * Runs a pass that produces the layer's new pixels into a scratch target, copies
 * it back and records the affected region for undo. Shaders leave pixels outside
 * the affected area bit-exact, so the recorded rectangle is enough.
 *
 * `rect` is in layer-local pixels (same space as the surface texture).
 */
export function applyToLayer(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  rect: Rect,
  draw: (target: RenderTarget, layerTexture: Texture) => void,
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  const surface = resources.ensureLayer(layerId).surface;
  const region = rectAlignToPixels(rect, doc.width, doc.height);
  if (region.width <= 0 || region.height <= 0) return null;

  const before = surface.readPixels(region);
  const scratch = resources.rgba(1);
  draw(scratch, surface.texture);
  engine.renderer.copy(surface.target.framebuffer, scratch.texture);
  const after = surface.readPixels(region);

  return [pixelOp(layerId, { rect: region, data: before }, { rect: region, data: after })];
}

/** Region a destructive edit can touch: the selection bounds, or the whole document. */
export function editableRegion(engine: Engine, doc: DocumentMeta): Rect {
  const resources = engine.resourcesFor(doc);
  return resources.selection.bounds() ?? FULL_RECT(doc);
}

// --- Paint ---

export function bucketFill(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  point: Point,
  color: RGBA,
  options: { tolerance: number; contiguous: boolean; opacity: number },
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  const surface = resources.ensureLayer(layerId).surface;
  const pixels = surface.readPixels();
  const offset = layerOffset(doc, layerId);

  const result = floodFill({
    pixels,
    width: doc.width,
    height: doc.height,
    startX: point.x - offset.x,
    startY: point.y - offset.y,
    tolerance: options.tolerance,
    contiguous: options.contiguous,
  });
  if (!result) return null;

  const coverage = resources.r8(1);
  coverage.clear(0, 0, 0, 0);
  coverage.texture.uploadPixels(FULL_RECT(doc), result.coverage);

  // Coverage is layer-local; selection stays document-space.
  return applyToLayer(engine, doc, layerId, result.bounds, (target, layerTexture) => {
    engine.renderer.applyStroke(
      target.framebuffer,
      layerTexture,
      coverage.texture,
      resources.selection.activeTexture,
      color,
      options.opacity,
      false,
      'normal',
      { contentOffset: offset, coverageDocSpace: false },
    );
  });
}

/** Fills the selection, or the whole layer when nothing is selected. */
export function fillSelection(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  color: RGBA,
  opacity = 1,
  blendMode: BlendMode = 'normal',
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  const selectionTexture = resources.selection.activeTexture;
  const coverage = resources.r8(1);
  if (selectionTexture) {
    engine.renderer.copy(coverage.framebuffer, selectionTexture);
  } else {
    coverage.clear(1, 1, 1, 1);
  }

  const offset = layerOffset(doc, layerId);
  const region = toLayerLocalRect(
    editableRegion(engine, doc),
    offset,
    doc.width,
    doc.height,
  );
  return applyToLayer(engine, doc, layerId, region, (target, layerTexture) => {
    engine.renderer.applyStroke(
      target.framebuffer,
      layerTexture,
      coverage.texture,
      null,
      color,
      opacity,
      false,
      blendMode,
      { contentOffset: offset },
    );
  });
}

/** Coverage of the current selection, or full white when nothing is selected. */
function selectionCoverage(engine: Engine, doc: DocumentMeta): RenderTarget {
  const resources = engine.resourcesFor(doc);
  const coverage = resources.r8(1);
  const selectionTexture = resources.selection.activeTexture;
  if (selectionTexture) engine.renderer.copy(coverage.framebuffer, selectionTexture);
  else coverage.clear(1, 1, 1, 1);
  return coverage;
}

/** Fills the selection with a repeating tile; the tile grid starts at the origin. */
export function fillSelectionWithPattern(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  pattern: PatternImage,
  options: { opacity: number; blendMode: BlendMode },
): EngineHistoryOp[] | null {
  const coverage = selectionCoverage(engine, doc);
  const tile = Texture.create(engine.gl, pattern.width, pattern.height, 'rgba8', 'nearest');
  tile.uploadPixels({ x: 0, y: 0, width: pattern.width, height: pattern.height }, pattern.data);

  const offset = layerOffset(doc, layerId);
  const region = toLayerLocalRect(
    editableRegion(engine, doc),
    offset,
    doc.width,
    doc.height,
  );
  try {
    return applyToLayer(engine, doc, layerId, region, (target, layerTexture) => {
      engine.renderer.applyPattern(target.framebuffer, layerTexture, coverage.texture, tile, {
        docWidth: doc.width,
        docHeight: doc.height,
        originX: 0,
        originY: 0,
        opacity: options.opacity,
        blendMode: options.blendMode,
        contentOffset: offset,
      });
    });
  } finally {
    tile.dispose();
  }
}

/**
 * Filter - Other - Offset. Wrapping is what makes a tile seamless: the seam moves
 * into view so it can be painted out.
 */
export function offsetLayer(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  dx: number,
  dy: number,
  wrap: boolean,
): EngineHistoryOp[] | null {
  if (dx === 0 && dy === 0) return null;
  return applyToLayer(engine, doc, layerId, FULL_RECT(doc), (target, layerTexture) => {
    engine.renderer.offset(
      target.framebuffer,
      layerTexture,
      dx / doc.width,
      dy / doc.height,
      wrap,
    );
  });
}

/** Erases the selection, or the whole layer when nothing is selected. */
export function clearSelection(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  const selectionTexture = resources.selection.activeTexture;
  const coverage = resources.r8(1);
  if (selectionTexture) {
    engine.renderer.copy(coverage.framebuffer, selectionTexture);
  } else {
    coverage.clear(1, 1, 1, 1);
  }

  const offset = layerOffset(doc, layerId);
  const region = toLayerLocalRect(
    editableRegion(engine, doc),
    offset,
    doc.width,
    doc.height,
  );
  return applyToLayer(engine, doc, layerId, region, (target, layerTexture) => {
    engine.renderer.applyStroke(
      target.framebuffer,
      layerTexture,
      coverage.texture,
      null,
      { r: 0, g: 0, b: 0, a: 1 },
      1,
      true,
      'normal',
      { contentOffset: offset },
    );
  });
}

// --- Gradient ---

export interface GradientFill {
  gradient: Gradient;
  type: GradientType;
  /** Both ends in document pixels; the drag direction sets the axis. */
  start: Point;
  end: Point;
  opacity: number;
  blendMode: BlendMode;
  reverse: boolean;
  dither: boolean;
}

/** Uploads the baked stops. One texture per fill is cheap next to the pass itself. */
function gradientLut(engine: Engine, gradient: Gradient): Texture {
  const data = gradientToLut(gradient);
  const width = data.length / 4;
  const texture = Texture.create(engine.gl, width, 1, 'rgba8', 'linear');
  texture.uploadPixels({ x: 0, y: 0, width, height: 1 }, data);
  return texture;
}

function drawGradientPass(
  engine: Engine,
  doc: DocumentMeta,
  target: RenderTarget,
  source: Texture,
  fill: GradientFill,
  contentOffset: Point,
): void {
  const resources = engine.resourcesFor(doc);
  const lut = gradientLut(engine, fill.gradient);
  engine.renderer.drawGradient(target.framebuffer, source, resources.selection.activeTexture, {
    type: GRADIENT_TYPE_IDS[fill.type],
    start: fill.start,
    end: fill.end,
    lut,
    opacity: fill.opacity,
    blendMode: fill.blendMode,
    reverse: fill.reverse,
    dither: fill.dither,
    contentOffset,
  });
  lut.dispose();
}

/** Live preview while the gradient is being dragged out. */
export function renderGradientPreview(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  fill: GradientFill,
): Texture {
  const resources = engine.resourcesFor(doc);
  const surface = resources.ensureLayer(layerId).surface;
  const target = resources.rgba(2);
  drawGradientPass(engine, doc, target, surface.texture, fill, layerOffset(doc, layerId));
  return target.texture;
}

export function applyGradient(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  fill: GradientFill,
): EngineHistoryOp[] | null {
  const offset = layerOffset(doc, layerId);
  const region = toLayerLocalRect(
    editableRegion(engine, doc),
    offset,
    doc.width,
    doc.height,
  );
  return applyToLayer(engine, doc, layerId, region, (target, layerTexture) => {
    drawGradientPass(engine, doc, target, layerTexture, fill, offset);
  });
}

// --- Healing ---

/**
 * Patch: heals the selected region from pixels `offset` away, which is the same
 * seamless-clone solve the healing brushes use.
 */
export function patchSelection(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  offset: Point,
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  const mask = resources.selection.activeTexture;
  const bounds = resources.selection.bounds();
  if (!mask || !bounds) return null;

  const contentOffset = layerOffset(doc, layerId);
  const localBounds = toLayerLocalRect(bounds, contentOffset, doc.width, doc.height);
  const target = surfaceStrokeTarget(resources, layerId);
  return applyToLayer(engine, doc, layerId, localBounds, (scratch) => {
    healRegion(
      {
        renderer: engine.renderer,
        resources,
        width: doc.width,
        height: doc.height,
        target,
        // The region itself is the mask, so clipping to the selection again
        // would leave the seam untouched.
        selection: null,
        contentOffset,
      },
      mask,
      bounds,
      { offsetX: offset.x, offsetY: offset.y },
      scratch,
    );
  });
}

// --- Clipboard ---

export interface CopiedPixels {
  /** Where the pixels came from, so Paste in Place can put them back. */
  rect: Rect;
  data: Uint8Array;
}

/**
 * Reads the selected part of a layer, or of the composite when `layerId` is
 * null. The selection is folded into alpha, so feathered edges survive a
 * round-trip through the clipboard.
 */
export function copyPixels(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string | null,
): CopiedPixels | null {
  const resources = engine.resourcesFor(doc);
  const rect = rectAlignToPixels(editableRegion(engine, doc), doc.width, doc.height);
  if (rect.width <= 0 || rect.height <= 0) return null;

  const source = layerId
    ? resources.ensureLayer(layerId).surface.texture
    : engine.compositeToTexture(doc, {
        view: { zoom: 1, centerX: 0, centerY: 0, rotation: 0 },
        showSelection: false,
      });
  const data = source.readPixels(rect);

  const selection = resources.selection.activeTexture;
  if (selection) {
    const coverage = selection.readPixels(rect);
    for (let i = 0; i < coverage.length; i += 1) {
      data[i * 4 + 3] = Math.round((data[i * 4 + 3] * coverage[i]) / 255);
    }
  }
  return { rect, data };
}

/** Draws loose RGBA pixels into a layer at a document-space position. */
export function drawPixelsIntoLayer(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  source: { width: number; height: number; data: Uint8Array },
  at: Point,
): void {
  const resources = engine.resourcesFor(doc);
  const surface = resources.ensureLayer(layerId).surface;
  const staging = Texture.create(engine.gl, source.width, source.height, 'rgba8', 'linear');
  staging.uploadPixels({ x: 0, y: 0, width: source.width, height: source.height }, source.data);
  const srcMatrix = placementMatrix(
    { x: at.x, y: at.y, width: source.width, height: source.height },
    doc.width,
    doc.height,
  );
  surface.target.clear(0, 0, 0, 0);
  engine.renderer.copy(surface.target.framebuffer, staging, { srcMatrix });
  staging.dispose();
}

// --- Selection edits ---

/** Paints a band along the selection edge, the Edit > Stroke command. */
export function strokeSelection(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  options: { thickness: number; location: StrokeLocation; color: RGBA; opacity: number },
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  const selection = resources.selection.activeTexture;
  if (!selection) return null;

  const band = strokeCoverage(
    selection.readPixels(),
    doc.width,
    doc.height,
    options.thickness,
    options.location,
  );
  const coverage = resources.r8(1);
  coverage.clear(0, 0, 0, 0);
  coverage.texture.uploadPixels(FULL_RECT(doc), band);

  // The band can reach outside the selection, so it must not be clipped by it.
  const offset = layerOffset(doc, layerId);
  const region = toLayerLocalRect(
    grow(resources.selection.bounds() ?? FULL_RECT(doc), options.thickness + 2),
    offset,
    doc.width,
    doc.height,
  );
  return applyToLayer(engine, doc, layerId, region, (target, layerTexture) => {
    engine.renderer.applyStroke(
      target.framebuffer,
      layerTexture,
      coverage.texture,
      null,
      options.color,
      options.opacity,
      false,
      'normal',
      { contentOffset: offset },
    );
  });
}

/** Select > Transform Selection: warps the mask itself, leaving pixels alone. */
export function transformSelectionMask(
  engine: Engine,
  doc: DocumentMeta,
  srcMatrix: Mat3,
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  const before = engine.snapshotSelection(doc);
  if (!before) return null;

  const scratch = resources.r8(2);
  scratch.clear(0, 0, 0, 0);
  engine.renderer.copy(scratch.framebuffer, resources.selection.texture, { srcMatrix });
  resources.selection.restore(scratch.texture.readPixels());

  return [selectionOp(before, engine.snapshotSelection(doc))];
}

function grow(rect: Rect, by: number): Rect {
  return {
    x: rect.x - by,
    y: rect.y - by,
    width: rect.width + by * 2,
    height: rect.height + by * 2,
  };
}

// --- Adjustments ---

export function renderAdjustmentPreview(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  state: AdjustmentState,
): Texture {
  const resources = engine.resourcesFor(doc);
  const surface = resources.ensureLayer(layerId).surface;
  const target = resources.rgba(2);
  const pass = adjustmentPass(engine, resources, state, surface.texture);
  engine.renderer.applyAdjustment(
    target.framebuffer,
    surface.texture,
    resources.selection.activeTexture,
    pass,
  );
  return target.texture;
}

export function applyAdjustment(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  state: AdjustmentState,
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  return applyToLayer(engine, doc, layerId, editableRegion(engine, doc), (target, layerTexture) => {
    const pass = adjustmentPass(engine, resources, state, layerTexture);
    engine.renderer.applyAdjustment(
      target.framebuffer,
      layerTexture,
      resources.selection.activeTexture,
      pass,
    );
  });
}

// --- Filters ---

export function renderFilterPreview(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  id: FilterId,
  values: Record<string, number>,
): Texture {
  const resources = engine.resourcesFor(doc);
  const surface = resources.ensureLayer(layerId).surface;
  const target = resources.rgba(2);
  const pass = filterPass(engine, resources, id, values, surface.texture, 1);
  engine.renderer.applyFilter(
    target.framebuffer,
    surface.texture,
    resources.selection.activeTexture,
    pass,
  );
  return target.texture;
}

export function applyFilter(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  id: FilterId,
  values: Record<string, number>,
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  return applyToLayer(engine, doc, layerId, editableRegion(engine, doc), (target, layerTexture) => {
    const pass = filterPass(engine, resources, id, values, layerTexture, Date.now() % 1000);
    engine.renderer.applyFilter(
      target.framebuffer,
      layerTexture,
      resources.selection.activeTexture,
      pass,
    );
  });
}

// --- Transform ---

/**
 * Bakes a move or free-transform into the layer. Snapshots the whole layer,
 * because the transform can pull content in from anywhere in the document.
 */
export function commitLayerTransform(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  srcMatrix: Mat3,
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  const surface = resources.ensureLayer(layerId).surface;
  const before: FullSnapshot = {
    width: surface.width,
    height: surface.height,
    data: surface.readPixels(),
  };

  const scratch = resources.rgba(1);
  engine.renderer.copy(scratch.framebuffer, surface.texture, { srcMatrix });
  engine.renderer.copy(surface.target.framebuffer, scratch.texture);

  const after: FullSnapshot = {
    width: surface.width,
    height: surface.height,
    data: surface.readPixels(),
  };
  const ops = [surfaceOp(layerId, before, after)];

  // A linked mask is part of the layer, so the same warp applies to it.
  const mask = resources.layer(layerId)?.mask;
  const linked = findLayerById(doc.layers, layerId)?.maskLinked ?? true;
  if (mask && linked) {
    const maskBefore: FullSnapshot = {
      width: mask.width,
      height: mask.height,
      data: mask.texture.readPixels(),
    };
    const maskScratch = resources.r8(3);
    maskScratch.clear(0, 0, 0, 0);
    engine.renderer.copy(maskScratch.framebuffer, mask.texture, { srcMatrix });
    engine.renderer.copy(mask.framebuffer, maskScratch.texture);
    ops.push(
      layerMaskOp(layerId, maskBefore, {
        width: mask.width,
        height: mask.height,
        data: mask.texture.readPixels(),
      }),
    );
  }

  return ops;
}

/** Grid resolution of a warp draw; fine enough that the patch reads as smooth. */
const WARP_CELLS = 32;
const WARP_PREVIEW_SLOT = 7;

/** Where a warp grid vertex comes from and where it goes, in document pixels. */
function warpSampler(rect: Rect, mesh: WarpMesh) {
  return (s: number, t: number) => {
    const point = warpPoint(mesh, s, t);
    return {
      dest: [point.x, point.y] as const,
      src: [rect.x + s * rect.width, rect.y + t * rect.height] as const,
    };
  };
}

/**
 * Draws a layer through a warp mesh into a scratch texture, for the live preview.
 * The result stands in for the layer's own pixels, so nothing is committed.
 */
export function renderWarpPreview(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  rect: Rect,
  mesh: WarpMesh,
): Texture | null {
  const surface = engine.resourcesFor(doc).layer(layerId)?.surface;
  if (!surface) return null;
  const target = engine.resourcesFor(doc).rgba(WARP_PREVIEW_SLOT);
  target.clear(0, 0, 0, 0);
  engine.renderer.warp(
    target.framebuffer,
    surface.texture,
    doc.width,
    doc.height,
    WARP_CELLS,
    warpSampler(rect, mesh),
  );
  return target.texture;
}

/** Bakes a warp into the layer, and into a linked mask along with it. */
export function commitLayerWarp(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  rect: Rect,
  mesh: WarpMesh,
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  const surface = resources.ensureLayer(layerId).surface;
  const before: FullSnapshot = {
    width: surface.width,
    height: surface.height,
    data: surface.readPixels(),
  };

  const sampler = warpSampler(rect, mesh);
  const scratch = resources.rgba(1);
  scratch.clear(0, 0, 0, 0);
  engine.renderer.warp(
    scratch.framebuffer,
    surface.texture,
    doc.width,
    doc.height,
    WARP_CELLS,
    sampler,
  );
  engine.renderer.copy(surface.target.framebuffer, scratch.texture);

  const ops = [
    surfaceOp(layerId, before, {
      width: surface.width,
      height: surface.height,
      data: surface.readPixels(),
    }),
  ];

  const mask = resources.layer(layerId)?.mask;
  const linked = findLayerById(doc.layers, layerId)?.maskLinked ?? true;
  if (mask && linked) {
    const maskBefore: FullSnapshot = {
      width: mask.width,
      height: mask.height,
      data: mask.texture.readPixels(),
    };
    const maskScratch = resources.r8(3);
    maskScratch.clear(0, 0, 0, 0);
    engine.renderer.warp(
      maskScratch.framebuffer,
      mask.texture,
      doc.width,
      doc.height,
      WARP_CELLS,
      sampler,
    );
    engine.renderer.copy(mask.framebuffer, maskScratch.texture);
    ops.push(
      layerMaskOp(layerId, maskBefore, {
        width: mask.width,
        height: mask.height,
        data: mask.texture.readPixels(),
      }),
    );
  }

  return ops;
}

// --- Layer content ---

/** Replaces a layer's pixels with a solid colour, ignoring the selection. */
export function fillLayerSolid(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  color: RGBA,
): void {
  const resources = engine.resourcesFor(doc);
  resources.ensureLayer(layerId).surface.target.clear(color.r, color.g, color.b, color.a);
}

export function copyLayerPixels(
  engine: Engine,
  doc: DocumentMeta,
  fromLayerId: string,
  toLayerId: string,
): void {
  const resources = engine.resourcesFor(doc);
  const from = resources.ensureLayer(fromLayerId);
  const to = resources.ensureLayer(toLayerId);
  engine.renderer.copy(to.surface.target.framebuffer, from.surface.texture);
  if (from.mask) {
    const mask = resources.ensureMask(toLayerId);
    engine.renderer.copy(mask.framebuffer, from.mask.texture);
  }
}

export function uploadImageToLayer(
  engine: Engine,
  doc: DocumentMeta,
  layerId: string,
  source: TexImageSource,
  placement?: { x: number; y: number; width: number; height: number },
): void {
  const resources = engine.resourcesFor(doc);
  const surface = resources.ensureLayer(layerId).surface;
  const staging = resources.rgba(3);

  // Load the bitmap into a scratch texture at its own size, then place it.
  staging.texture.uploadSource(source);
  const box = placement ?? {
    x: 0,
    y: 0,
    width: staging.texture.width,
    height: staging.texture.height,
  };
  const srcMatrix = placementMatrix(box, doc.width, doc.height);
  surface.target.clear(0, 0, 0, 0);
  engine.renderer.copy(surface.target.framebuffer, staging.texture, { srcMatrix });
  // The staging target is document-sized again for later reuse.
  staging.resize(doc.width, doc.height);
}

/**
 * Maps destination UV of the document to source UV of an image placed at `box`
 * in document pixels. Anything outside the box samples out of range and is
 * discarded by `copy.frag`.
 */
export function placementMatrix(
  box: { x: number; y: number; width: number; height: number },
  docWidth: number,
  docHeight: number,
): Mat3 {
  const sx = docWidth / box.width;
  const sy = docHeight / box.height;
  return new Float32Array([sx, 0, 0, 0, sy, 0, -box.x / box.width, -box.y / box.height, 1]);
}

export function mergeLayerDown(
  engine: Engine,
  doc: DocumentMeta,
  upperLayerId: string,
  lowerLayerId: string,
): EngineHistoryOp[] | null {
  const resources = engine.resourcesFor(doc);
  const upper = findLayerById(doc.layers, upperLayerId);
  const lower = resources.ensureLayer(lowerLayerId);
  const upperResources = resources.layer(upperLayerId);
  if (!upper || !upperResources) return null;

  const before: FullSnapshot = {
    width: lower.surface.width,
    height: lower.surface.height,
    data: lower.surface.readPixels(),
  };

  const composited = engine.renderer.composite(
    [
      { texture: lower.surface.texture, mask: null, opacity: 1, blendMode: 'normal' },
      {
        texture: upperResources.surface.texture,
        mask: upper.hasMask && upper.maskEnabled ? (upperResources.mask?.texture ?? null) : null,
        opacity: upper.opacity,
        blendMode: upper.blendMode,
      },
    ],
    resources.pingPong,
  );
  engine.renderer.copy(lower.surface.target.framebuffer, composited);

  const after: FullSnapshot = {
    width: lower.surface.width,
    height: lower.surface.height,
    data: lower.surface.readPixels(),
  };
  return [surfaceOp(lowerLayerId, before, after)];
}

/** Composites the visible document into `targetLayerId`. */
export function flattenInto(
  engine: Engine,
  doc: DocumentMeta,
  targetLayerId: string,
): EngineHistoryOp[] {
  const resources = engine.resourcesFor(doc);
  const target = resources.ensureLayer(targetLayerId);
  const before: FullSnapshot = {
    width: target.surface.width,
    height: target.surface.height,
    data: target.surface.readPixels(),
  };

  const composited = engine.compositeToTexture(doc, {
    view: { zoom: 1, centerX: 0, centerY: 0, rotation: 0 },
    showSelection: false,
  });
  engine.renderer.copy(target.surface.target.framebuffer, composited);

  const after: FullSnapshot = {
    width: target.surface.width,
    height: target.surface.height,
    data: target.surface.readPixels(),
  };
  return [surfaceOp(targetLayerId, before, after)];
}

// --- Document geometry ---

/** Image Size: rescales every layer to the new pixel dimensions. */
export function resampleDocument(
  engine: Engine,
  doc: DocumentMeta,
  newWidth: number,
  newHeight: number,
): EngineHistoryOp[] {
  const resources = engine.resourcesFor(doc);
  const ops: EngineHistoryOp[] = [];
  const temp = new RenderTarget(engine.gl, newWidth, newHeight, 'rgba8', 'linear');

  for (const layerId of surfaceLayerIds(doc.layers)) {
    const layerResources = resources.ensureLayer(layerId);
    const surface = layerResources.surface;
    const before: FullSnapshot = {
      width: surface.width,
      height: surface.height,
      data: surface.readPixels(),
    };

    // UV space is normalised, so a plain blit into a different-sized target is
    // exactly a bilinear rescale.
    engine.renderer.copy(temp.framebuffer, surface.texture);
    surface.resize(newWidth, newHeight);
    engine.renderer.copy(surface.target.framebuffer, temp.texture);

    ops.push(
      surfaceOp(layerId, before, {
        width: newWidth,
        height: newHeight,
        data: surface.readPixels(),
      }),
    );

    if (layerResources.mask) {
      const maskTemp = new RenderTarget(engine.gl, newWidth, newHeight, 'r8', 'linear');
      engine.renderer.copy(maskTemp.framebuffer, layerResources.mask.texture);
      layerResources.mask.resize(newWidth, newHeight);
      engine.renderer.copy(layerResources.mask.framebuffer, maskTemp.texture);
      maskTemp.dispose();
    }
  }

  temp.dispose();
  resources.resize(newWidth, newHeight);
  return ops;
}

/**
 * Canvas Size and Crop: keeps pixel scale, moving content by `offset` (the
 * position of the old origin inside the new canvas).
 */
export function recanvasDocument(
  engine: Engine,
  doc: DocumentMeta,
  newWidth: number,
  newHeight: number,
  offsetX: number,
  offsetY: number,
): EngineHistoryOp[] {
  const resources = engine.resourcesFor(doc);
  const ops: EngineHistoryOp[] = [];
  const srcMatrix = new Float32Array([
    newWidth / doc.width,
    0,
    0,
    0,
    newHeight / doc.height,
    0,
    -offsetX / doc.width,
    -offsetY / doc.height,
    1,
  ]);

  const temp = new RenderTarget(engine.gl, newWidth, newHeight, 'rgba8', 'linear');
  for (const layerId of surfaceLayerIds(doc.layers)) {
    const layerResources = resources.ensureLayer(layerId);
    const surface = layerResources.surface;
    const before: FullSnapshot = {
      width: surface.width,
      height: surface.height,
      data: surface.readPixels(),
    };

    temp.clear(0, 0, 0, 0);
    engine.renderer.copy(temp.framebuffer, surface.texture, { srcMatrix });
    surface.resize(newWidth, newHeight);
    engine.renderer.copy(surface.target.framebuffer, temp.texture);

    ops.push(
      surfaceOp(layerId, before, {
        width: newWidth,
        height: newHeight,
        data: surface.readPixels(),
      }),
    );

    if (layerResources.mask) {
      const maskTemp = new RenderTarget(engine.gl, newWidth, newHeight, 'r8', 'linear');
      maskTemp.clear(0, 0, 0, 0);
      engine.renderer.copy(maskTemp.framebuffer, layerResources.mask.texture, { srcMatrix });
      layerResources.mask.resize(newWidth, newHeight);
      engine.renderer.copy(layerResources.mask.framebuffer, maskTemp.texture);
      maskTemp.dispose();
    }
  }

  temp.dispose();
  resources.resize(newWidth, newHeight);
  return ops;
}
